import { Box, CircularProgress, Typography } from "@mui/material";
import { STAGE_COLOR } from "../data/mockData";
import { fmtDias } from "../utils/format";
import {
  TE_CARD_SX,
  TE_COLORS,
  TE_PANEL_DESC_SX,
  TE_PANEL_TITLE_SX,
} from "./theme";

interface StageChartEntry {
  nome: string;
  icon: string;
  colorIndex: number;
  avgConcluidos: number | null;
  countConcluidos: number;
  avgAndamento: number | null;
  countAndamento: number;
  maxShared: number;
}

interface StageChartProps {
  data: StageChartEntry[];
  loading?: boolean;
}

export function StageChart({ data, loading }: StageChartProps) {
  return (
    <Box sx={TE_CARD_SX}>
      <Typography sx={{ ...TE_PANEL_TITLE_SX, mb: 0.5 }}>
        Tempo médio por etapa
      </Typography>
      <Typography sx={{ ...TE_PANEL_DESC_SX, mb: 1.5 }}>
        Tempo médio de quem já concluiu a etapa vs. tempo médio (até agora) de
        quem está parado nela.
      </Typography>

      {loading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box display="flex" gap={3} mb={1.5}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: TE_COLORS.blue,
                }}
              />
              <Typography
                sx={{ fontSize: 11, color: TE_COLORS.textDim, fontWeight: 600 }}
              >
                Concluídos
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: TE_COLORS.red,
                }}
              />
              <Typography
                sx={{ fontSize: 11, color: TE_COLORS.textDim, fontWeight: 600 }}
              >
                Em andamento (até agora)
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1.25}>
            {data.map((entry) => {
              const color = STAGE_COLOR[entry.colorIndex % STAGE_COLOR.length];
              const pctHist = entry.avgConcluidos
                ? (entry.avgConcluidos / entry.maxShared) * 100
                : 0;
              const pctAnda = entry.avgAndamento
                ? (entry.avgAndamento / entry.maxShared) * 100
                : 0;
              return (
                <Box
                  key={entry.nome}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr 1fr",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "6px",
                        bgcolor: `${color}22`,
                        color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9.5,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {entry.icon}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: TE_COLORS.text }}>
                      {entry.nome}
                    </Typography>
                  </Box>

                  <Bar
                    pct={pctHist}
                    color={TE_COLORS.blue}
                    label={
                      entry.countConcluidos ? fmtDias(entry.avgConcluidos) : "–"
                    }
                  />
                  <Bar
                    pct={pctAnda}
                    color={TE_COLORS.red}
                    label={
                      entry.countAndamento ? fmtDias(entry.avgAndamento) : "–"
                    }
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

function Bar({
  pct,
  color,
  label,
}: {
  pct: number;
  color: string;
  label: string;
}) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          flex: 1,
          height: 14,
          borderRadius: "5px",
          bgcolor: "#EEF0F6",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: "5px",
            width: `${pct}%`,
            bgcolor: color,
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          color: TE_COLORS.textDim,
          width: 52,
          flexShrink: 0,
          textAlign: "right",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
